import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { ConfirmDialog, Heading, Button } from '../../../shared/ui';
import { IJournalResult } from '../../../store/types';

import { availableFigures, Figure, PlacedFigure } from './types';
import { FigureRenderer } from './FigureRenderer';
import { SandTherapyBoard } from './SandTherapyBoard';

interface SandTherapyPsyProps {
  roomName?: string;
  sendSandTherapyOpen?: () => void;
  sendSandTherapyClose?: () => void;
  placedFigures: PlacedFigure[];
  sandColor: string;
  clientReady: boolean;
  onCloseTool?: () => void;
  result?: IJournalResult;
  setResult?: (value: IJournalResult) => void;
  /**
   * Мобильный 1:1: запуск — в нижней шторке, запущенная работа клиента — поле на весь экран.
   * Завершение с сохранением в журнал — кнопкой в самой панели.
   */
  mobile?: boolean;
}

const FIGURE_CATEGORIES = [
  { label: 'Семья — Пожилые', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-elder' },
  { label: 'Семья — Взрослые', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-adult' },
  { label: 'Семья — Подростки', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-teen' },
  { label: 'Семья — Дети', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-child' },
  { label: 'Семья — Младенцы', filter: (f: Omit<Figure, 'id'>) => f.type === 'family-baby' },
  { label: 'Эмоции — Спокойные', filter: (f: Omit<Figure, 'id'>) => f.type === 'emotional-calm' },
  { label: 'Эмоции — Расстроенные', filter: (f: Omit<Figure, 'id'>) => f.type === 'emotional-upset' },
  { label: 'Эмоции — Радостные', filter: (f: Omit<Figure, 'id'>) => f.type === 'emotional-happy' },
  { label: 'Дома', filter: (f: Omit<Figure, 'id'>) => f.type === 'house' },
  { label: 'Деревья', filter: (f: Omit<Figure, 'id'>) => f.type === 'tree' },
  {
    label: 'Животные',
    filter: (f: Omit<Figure, 'id'>) => ['animal-dog', 'animal-cat', 'animal-bird'].includes(f.type),
  },
  { label: 'Специальные', filter: (f: Omit<Figure, 'id'>) => ['cocoon', 'striped'].includes(f.type) },
  { label: 'Люди', filter: (f: Omit<Figure, 'id'>) => f.type === 'person' || f.type === 'tall' },
  { label: 'Шары', filter: (f: Omit<Figure, 'id'>) => f.type === 'ball' },
];

function getFigureHeightClass(size: Figure['size']) {
  switch (size) {
    case 'small':
      return 'h-10';
    case 'medium':
      return 'h-14';
    case 'large':
      return 'h-16';
  }
}

export const SandTherapyPsy: FC<SandTherapyPsyProps> = ({
  sendSandTherapyOpen,
  sendSandTherapyClose,
  placedFigures,
  sandColor,
  clientReady,
  onCloseTool,
  result,
  setResult,
  mobile = false,
}) => {
  const dispatch = useAppDispatch();
  const [localActive, setActive] = useState(false);
  // На телефоне панель переезжает из шторки (запуск) в основную область (поле) и пересоздаётся,
  // поэтому «запущено» берём ещё и из состояния инструмента, а не только из локального флага
  const isToolRunning = useAppSelector((state) => state.activeToolStateSlice.activeTool === PsyToolsId.SAND_THERAPY);
  const active = localActive || (mobile && isToolRunning);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);

  const handleStartSession = useCallback(() => {
    sendSandTherapyOpen?.();
    dispatch(setActiveTool(PsyToolsId.SAND_THERAPY));
    setActive(true);
  }, [sendSandTherapyOpen, dispatch]);

  const handleRequestClose = useCallback(() => {
    setIsConfirmCloseOpen(true);
  }, []);

  const handleConfirmClose = useCallback(() => {
    if (result && setResult && placedFigures.length > 0) {
      setResult({
        ...result,
        sand_therapy: [
          ...(result.sand_therapy ?? []),
          {
            date_time: new Date().toISOString(),
            placed_figures: placedFigures,
            sand_color: sandColor,
            comment: '',
          },
        ],
      });
    }
    setIsConfirmCloseOpen(false);
    sendSandTherapyClose?.();
    dispatch(setActiveTool(0));
    setActive(false);
    onCloseTool?.();
  }, [result, setResult, placedFigures, sandColor, sendSandTherapyClose, dispatch, onCloseTool]);

  const handleCancelClose = useCallback(() => {
    setIsConfirmCloseOpen(false);
  }, []);

  const previewFigures = useMemo(() => availableFigures.slice(0, 12), []);

  const closeModal = (
    <ConfirmDialog
      isOpen={isConfirmCloseOpen}
      title="Закрытие инструмента"
      message="Вы действительно хотите закрыть инструмент «Песочная терапия»? Клиент перестанет видеть инструмент."
      cancelText="Оставить открытым"
      confirmText="Закрыть инструмент"
      onCancel={handleCancelClose}
      onConfirm={handleConfirmClose}
    />
  );

  if (mobile && active) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col bg-ink-muted px-3 pb-3 pt-1 text-white">
        <div className="min-h-0 flex-1">
          <SandTherapyBoard
            mobile
            selectedFigures={[]}
            placedFigures={placedFigures}
            onPlaceFigure={() => {}}
            onMoveFigure={() => {}}
            onRotateFigure={() => {}}
            onResizeFigure={() => {}}
            onRemoveFigure={() => {}}
            sandColor={sandColor}
            readonly
          />
        </div>
        <div className="shrink-0 pt-3">
          {clientReady ? (
            <>
              <div className="mb-2 w-full rounded-control border border-success-line bg-success-surface px-3 py-2 text-center text-ui-strong text-success">
                Клиент завершил работу
              </div>
              <Button size="lg" fullWidth onClick={handleRequestClose}>
                Завершить работу с инструментом
              </Button>
            </>
          ) : (
            <Button size="lg" fullWidth loading>
              Клиент работает…
            </Button>
          )}
        </div>
        {closeModal}
      </div>
    );
  }

  if (mobile) {
    return (
      <div className="flex w-full flex-col gap-3 text-white">
        <p className="text-ui leading-relaxed text-white/90">
          Предложите клиенту выбрать фигуры из набора и расставить их на виртуальном песочном поле. Полученная
          расстановка служит проекцией для дальнейшей терапевтической беседы.
        </p>
        <div className="grid grid-cols-4 gap-2">
          {previewFigures.map((fig, idx) => (
            <div key={idx} className="flex h-16 items-center justify-center rounded-control bg-white/10 p-2">
              <div className={classNames('w-8', getFigureHeightClass(fig.size))}>
                <FigureRenderer figure={fig} className="h-full w-full" />
              </div>
            </div>
          ))}
          <div className="flex h-16 items-center justify-center rounded-control bg-white/10 p-2 text-center text-ui-xs text-white/50">
            +{availableFigures.length - previewFigures.length} фигур
          </div>
        </div>
        <div className="sticky bottom-[-1rem] z-40 -mx-4 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
          <Button size="lg" fullWidth onClick={handleStartSession}>
            Начать сессию
          </Button>
        </div>
      </div>
    );
  }

  if (active) {
    return (
      <div className="w-[1200px] min-w-[1200px] h-full flex flex-col text-white p-6 relative">
        <Heading tone="inverse" size="lg" className="mb-4">
          Песочная терапия
        </Heading>

        <div className="flex-1 min-h-0 rounded-control overflow-hidden">
          <SandTherapyBoard
            selectedFigures={[]}
            placedFigures={placedFigures}
            onPlaceFigure={() => {}}
            onMoveFigure={() => {}}
            onRotateFigure={() => {}}
            onResizeFigure={() => {}}
            onRemoveFigure={() => {}}
            sandColor={sandColor}
            readonly
          />
        </div>

        <div className="absolute bottom-6 left-6 right-6 flex gap-3">
          {clientReady ? (
            <div className="flex-1 flex-col items-center justify-center px-2 py-2">
              <div className="w-full bg-success-surface border border-success text-success px-3 py-2 rounded-control text-body-l-strong text-center mb-1.5">
                Клиент завершил работу
              </div>
              <Button size="lg" fullWidth onClick={handleRequestClose}>
                Завершить работу с инструментом
              </Button>
            </div>
          ) : (
            <div className="flex-1 flex-col items-center justify-center px-2 py-2">
              <Button size="lg" fullWidth loading>
                Клиент работает…
              </Button>
            </div>
          )}
        </div>

        <ConfirmDialog
          isOpen={isConfirmCloseOpen}
          title="Закрытие инструмента"
          message="Вы действительно хотите закрыть инструмент «Песочная терапия»? Клиент перестанет видеть инструмент."
          cancelText="Оставить открытым"
          confirmText="Закрыть инструмент"
          onCancel={handleCancelClose}
          onConfirm={handleConfirmClose}
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col text-white pt-6 px-6 relative">
      <p className="text-display-s mb-4">Песочная терапия</p>
      <div className="text-body-l leading-relaxed mb-6">
        Предложите клиенту выбрать фигуры из набора и расставить их на виртуальном песочном поле.
        <br />
        <br />
        Полученная расстановка служит проекцией для дальнейшей терапевтической беседы.
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        <div className="grid grid-cols-4 gap-2 pb-[100px]">
          {previewFigures.map((fig, idx) => (
            <div key={idx} className="flex items-center justify-center bg-white/10 rounded-control p-2 h-16">
              <div className={classNames('w-8', getFigureHeightClass(fig.size))}>
                <FigureRenderer figure={fig} className="w-full h-full" />
              </div>
            </div>
          ))}
          <div className="flex items-center justify-center bg-white/10 rounded-control p-2 h-16 text-white/50 text-ui-xs text-center">
            +{availableFigures.length - previewFigures.length} фигур
          </div>
        </div>
      </div>

      <div className="absolute bottom-[24px] left-6 right-8">
        <Button size="lg" fullWidth onClick={handleStartSession}>
          Начать сессию
        </Button>
      </div>
    </div>
  );
};
